// Figma: 6643:30815 — see stories/blocks/bordered-frame.stories.tsx
// White bordered container on grey-50 — shared across redesign pages.
// Connector placement follows page-rail junctions — see `/grid-connectors` skill.

import {
  GRID_ELBOW_FRAME_BOTTOM_LEFT,
  GRID_ELBOW_FRAME_BOTTOM_RIGHT,
  GRID_ELBOW_FRAME_TOP_LEFT,
  GRID_ELBOW_FRAME_TOP_RIGHT,
  GridElbow,
} from "@/components/ui/grid-connector";
import { cn } from "@/lib/utils";

/**
 * Which rail junction this bordered frame marks on the page grid.
 *
 * Read from Figma per frame — do not hardcode by section name:
 * - `rail-top`: L marks on the **top** border (y ≈ 0 inside Container).
 * - `rail-bottom`: L marks on the **bottom** border (y ≈ container height).
 */
export type RailConnectorEdge = "rail-top" | "rail-bottom" | "rail-both";

interface BorderedFrameProps {
  children: React.ReactNode;
  className?: string;
  /** Grid-connector L marks at the rail junction edge of this frame. */
  connectors?: RailConnectorEdge | "none";
  /** Collapse the top border with the previous white panel (stacked containers). */
  stack?: boolean;
}

/** White bordered container — square corners, 64px inner padding at lg by default. */
export function BorderedFrame({
  children,
  className,
  connectors = "none",
  stack = false,
}: BorderedFrameProps) {
  return (
    <div
      className={cn(
        "border-grey-100 bg-grey-0 relative mx-auto w-full max-w-[1280px] border px-8 py-12 md:px-16 md:py-16",
        stack && "-mt-px border-t-0",
        className
      )}
    >
      {connectors === "rail-top" || connectors === "rail-both" ? (
        <>
          <GridElbow
            corner="top-left"
            className={cn(GRID_ELBOW_FRAME_TOP_LEFT, "z-10 hidden lg:block")}
          />
          <GridElbow
            corner="top-right"
            className={cn(GRID_ELBOW_FRAME_TOP_RIGHT, "z-10 hidden lg:block")}
          />
        </>
      ) : null}
      {connectors === "rail-bottom" || connectors === "rail-both" ? (
        <>
          <GridElbow
            corner="bottom-left"
            className={cn(GRID_ELBOW_FRAME_BOTTOM_LEFT, "z-10 hidden lg:block")}
          />
          <GridElbow
            corner="bottom-right"
            className={cn(
              GRID_ELBOW_FRAME_BOTTOM_RIGHT,
              "z-10 hidden lg:block"
            )}
          />
        </>
      ) : null}
      {children}
    </div>
  );
}
